import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { LuClock, LuSearch } from 'react-icons/lu';
import { AiOutlineAppstore } from 'react-icons/ai';
import { MdOutlineArrowForward } from 'react-icons/md';

// Ленивое импортирование компонентов
const Navbar = React.lazy(() => import('../../components/navbar'));
const Footer = React.lazy(() => import('../../components/footer'));
const DiscoverItems = React.lazy(() => import('../../components/discover-items'));

const options = [
  { value: '', label: 'All' },
  { value: 'Art', label: 'Art' },
  { value: 'Moto', label: 'Moto' },
  { value: 'Animals', label: 'Animals' },
  { value: 'Robots', label: 'Robots' },
  { value: 'Cube', label: 'Cube' },
  { value: 'Tube', label: 'Tube' },
  { value: 'Cyber', label: 'Cyber' },
  { value: 'Anime', label: 'Anime' },
];

const forSale = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Buy Now' },
  { value: 'auctions', label: 'Auctions' },
  { value: 'offers', label: 'Offers' },
];

const rateOptions = [
  { value: '', label: 'All' },
  { value: 'top', label: 'Top Rated' },
  { value: 'low', label: 'Lower Rated' },
];

// Настройка стилей для Select (мемоизирована)
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#1e293b',
    borderColor: state.isFocused ? '#4c51bf' : '#1e40af',
    color: '#ffffff',
    minHeight: '44px',
    borderRadius: '8px',
    boxShadow: state.isFocused ? '0 0 5px rgba(76, 81, 191, 0.5)' : 'none',
    '&:hover': { borderColor: '#4c51bf' },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#4c51bf' : '#1e293b',
    color: state.isFocused ? '#ffffff' : '#94a3b8',
    cursor: 'pointer',
    padding: '12px 16px',
  }),
  singleValue: (provided) => ({ ...provided, color: '#ffffff' }),
  menu: (provided) => ({ ...provided, backgroundColor: '#1e293b', zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  placeholder: (provided) => ({ ...provided, color: '#94a3b8' }),
};

const ITEMS_PER_PAGE = 8;

export default function ExploreOne() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(options[0]);
  const [selectedForSale, setSelectedForSale] = useState(forSale[0]);
  const [rateOption, setRateOption] = useState(rateOptions[0]);
  const [nfts, setNfts] = useState([]);

  // Debounce для поиска (300 мс)
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const fetchNFTsWithFilters = async () => {
      try {
        const params = {
          search: debouncedSearch || undefined,
          category: category?.value || undefined,
          forSale: selectedForSale?.value === 'true' ? true : undefined,
        };
        const response = await axios.get('https://nifty-art.com/api/nfts/market', { params });
        const nftsWithOwners = await Promise.all(
          response.data.map(async (nft) => {
            try {
              const ownerResponse = await axios.get(`https://nifty-art.com/api/users/change/${nft.owner}`);
              return {
                ...nft,
                ownerUsername: ownerResponse.data.username,
                ownerAvatar: ownerResponse.data.avatar || '/assets/images/avatar/2.jpg',
              };
            } catch (error) {
              return {
                ...nft,
                ownerUsername: 'Unknown Owner',
                ownerAvatar: '/assets/images/avatar/2.jpg',
              };
            }
          })
        );
        // Если forSale фильтр включён, отфильтруем по forSale === true
        const filteredNFTs =
          selectedForSale?.value === 'true'
            ? nftsWithOwners.filter((nft) => nft.forSale === true)
            : nftsWithOwners;
        setNfts(filteredNFTs);
      } catch (error) {
        console.error('Error fetching NFTs:', error);
      }
    };

    fetchNFTsWithFilters();
  }, [debouncedSearch, category, selectedForSale]);

  // Оптимизация фонового изображения: используйте оптимизированный файл и inline-стиль
  const heroStyle = useMemo(() => ({
    backgroundImage: "url('/assets/images/bg/bg1.svg')", // убедитесь, что изображение оптимизировано/сжатое или загружается через CDN
    backgroundSize: 'cover',
    backgroundPosition: 'bottom',
    backgroundRepeat: 'no-repeat',
  }), []);

  return (
    <React.Suspense fallback={<div>Loading...</div>}>
      <Navbar />
      <section className="relative table w-full py-36" style={heroStyle}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900"></div>
        <div className="container">
          <div className="grid grid-cols-1 pb-8 text-center mt-10">
            <h3 className="md:text-3xl text-2xl md:leading-snug tracking-wide leading-snug font-medium text-white">
              Explore Items
            </h3>
          </div>
        </div>
      </section>
      <div className="relative">
        <div className="shape absolute start-0 end-0 sm:-bottom-px -bottom-[2px] overflow-hidden z-1 text-white dark:text-slate-900">
          <svg className="w-full h-auto" viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
          </svg>
        </div>
      </div>

      <section className="relative md:pb-24 pb-16 -mt-16">
        <div className="container z-10">
          <div className="grid grid-cols-1">
            <form className="p-6 bg-shadow-gray-700 dark:bg-slate-900 rounded-xl shadow dark:shadow-gray-700">
              <div className="registration-form text-dark text-start">
                <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 grid-cols-1 gap-6">
                  {/* Search Input */}
                  <div>
                    <label className="form-label font-medium dark:text-white">Search:</label>
                    <div className="relative filter-border mt-2">
                      <LuSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        name="name"
                        type="text"
                        id="job-keyword"
                        className="form-input w-full filter-input-box pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500"
                        placeholder="Search your keywords"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>
                  {/* Categories */}
                  <div>
                    <label className="form-label font-medium dark:text-white">Categories:</label>
                    <div className="relative filter-border mt-2">
                      <AiOutlineAppstore className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Select
                        className="form-input dark:bg-slate-800 bg-gray-50 text-white border-gray-600"
                        options={options}
                        value={category}
                        onChange={setCategory}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                  {/* Items */}
                  <div>
                    <label className="form-label font-medium dark:text-white">Items:</label>
                    <div className="relative filter-border mt-2">
                      <LuClock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <Select
                        className="form-input dark:bg-slate-800 bg-gray-50 text-white border-gray-600"
                        options={forSale}
                        value={selectedForSale}
                        onChange={setSelectedForSale}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        <DiscoverItems nfts={nfts} pagination={true} title="Discover Items" />
      </section>
      <Footer />
    </React.Suspense>
  );
}

